// Derive a project's lifecycle status from LIVE facts, not the stored
// `project.status` enum (which is a snapshot the last action wrote and drifts
// out of sync — e.g. a book that's fully proofed + reviewed still read "ready to
// proof" because an exclude/boundary edit reset the stored value).
//
// The ladder, furthest stage first reached:
//   Setup → Mapping → Proofing → Proofed → In review → Reviewed
// Review completeness lives in the browser (dismissals in localStorage + server
// pickups), so "In review"/"Reviewed" only resolve where a tally is passed (the
// project page). The list tops out at "Proofed" + the badges, which is accurate.
//
// Badges are orthogonal annotations: "✓ Spec" (a spec-check ran) and "★ N"
// (pickups to address).
import type { Project } from "../api/types";
import type { ReviewTally } from "./reportStore";

export interface DerivedStatus {
  key: "setup" | "mapping" | "proofing" | "proofed" | "in_review" | "reviewed";
  label: string;
  badges: { icon: string; label: string; title: string }[];
}

export function deriveProjectStatus(
  p: Project,
  opts?: { tally?: ReviewTally | null; pickups?: number }
): DerivedStatus {
  const pickups = opts?.pickups ?? p.n_pickups ?? 0;
  const specChecked = !!p.workflows?.audio_analysis;
  const badges: DerivedStatus["badges"] = [];
  if (specChecked) badges.push({ icon: "✓", label: "Spec", title: "Spec check complete" });
  if (pickups > 0)
    badges.push({ icon: "★", label: String(pickups), title: `${pickups} pickup${pickups === 1 ? "" : "s"} to address` });

  let key: DerivedStatus["key"] = "setup";
  let label = "Setup";

  if (!p.has_manuscript || p.n_audio === 0) {
    key = "setup"; label = "Setup"; // missing the manuscript or any audio
  } else if (p.n_chapters === 0) {
    // Audio + manuscript present but the mapping isn't confirmed yet (chapters
    // are materialised at confirm time).
    key = "mapping";
    label = p.status === "mapping_proposed" ? "Review mapping" : "Mapping";
  } else {
    const proofedDone = p.proofed + p.n_excluded >= p.n_chapters;
    if (p.proofing_active || !proofedDone) {
      key = "proofing"; label = "Proofing";
    } else {
      const t = opts?.tally;
      if (t && t.total > 0) {
        const reviewed = t.picked + t.dismissed;
        if (reviewed >= t.total) { key = "reviewed"; label = "Reviewed"; }
        else if (reviewed > 0) { key = "in_review"; label = "In review"; }
        else { key = "proofed"; label = "Proofed"; }
      } else {
        key = "proofed"; label = "Proofed";
      }
    }
  }
  return { key, label, badges };
}
